// src/components/materials/MaterialsLibrary.tsx
//
// Every material this workspace made from CRM - the Documents surface.
//
// ============================================================================
// THIS IS A FEDERATED VIEW, NOT AN INDEX
// ============================================================================
//
// It does not list CRM files. It asks PitchLess "what did this workspace
// create from CRM?" and renders the answer. CRM holds no row per material, so
// there is nothing here that can fall out of step: a material deleted in
// PitchLess simply stops appearing, and one created there appears without CRM
// being told.
//
// The alternative - writing a FileAsset per material so it shows up in the
// existing file list - was rejected deliberately. It would put a second,
// stale copy of a versioned document into a system that does not own it, and
// every one of those copies would need a sync path that could fail silently.
// A material is not a CRM file. It is a PitchLess artifact that CRM knows
// where to find.
//
// Because of that, this tab sits BESIDE "My Files" rather than inside it.
// Mixing them would imply a shared lifecycle they do not have: you cannot
// rename, re-share or delete a material here, and pretending otherwise would
// be worse than the extra tab.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetMaterialsQuery,
  useGetMaterialsConfigQuery,
  materialErrorCode,
  isBusy,
  type Material,
  type MaterialSurface,
  type MaterialReviewStatus,
} from '../../api/materialsApi';
import { MaterialStatusBadge } from './MaterialStatusBadge';
import { useMaterialFile } from './useMaterialFile';

const SURFACE_LABEL: Record<MaterialSurface, string> = {
  estimate: 'Estimate',
  opportunity: 'Opportunity',
  company: 'Company',
};

/**
 * Where a material came from, as a link back into CRM.
 *
 * Built from the provenance PitchLess stored, so it survives the material
 * being opened from anywhere. Returns null for a surface this build does not
 * know about rather than guessing a route - a wrong link is worse than none.
 */
function sourceRoute(m: Material): string | null {
  if (!m.sourceEntityId) return null;
  switch (m.sourceEntityType) {
    case 'company':
      return `/companies/${m.sourceEntityId}`;
    case 'opportunity':
      return `/pipeline`;
    case 'estimate':
      return `/payments`;
    default:
      return null;
  }
}

const REVIEW_FILTERS: Array<{ value: MaterialReviewStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'drafting', label: 'Drafts' },
  { value: 'in-review', label: 'In review' },
  { value: 'needs-revision', label: 'Changes requested' },
  { value: 'approved', label: 'Approved' },
];

export function MaterialsLibrary(): React.ReactElement {
  const { data: config } = useGetMaterialsConfigQuery();
  const [review, setReview] = useState<MaterialReviewStatus | 'all'>('all');
  const [surface, setSurface] = useState<MaterialSurface | 'all'>('all');
  const { download, pending: filePending } = useMaterialFile();

  const { data, isLoading, isError, error, refetch } = useGetMaterialsQuery(
    {
      ...(review !== 'all' ? { reviewStatus: review } : {}),
      ...(surface !== 'all' ? { entityType: surface } : {}),
    },
    { skip: !config?.configured }
  );

  const materials = useMemo(() => data?.artifacts ?? [], [data]);

  if (config && !config.configured) {
    return (
      <p className="py-8 text-sm text-gray-500">
        PitchLess is not connected in this environment.
      </p>
    );
  }

  return (
    <div className="w-full max-w-full space-y-4" data-testid="materials-library">
      {/* Filters. Both are sent to PitchLess rather than applied here: the
          list is its answer, and filtering a page of it locally would show a
          different total than the one the filter implies. */}
      <div className="flex flex-wrap gap-3">
        <div>
          <label htmlFor="materials-review" className="sr-only">
            Filter by review state
          </label>
          <select
            id="materials-review"
            value={review}
            onChange={(e) => setReview(e.target.value as MaterialReviewStatus | 'all')}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {REVIEW_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="materials-surface" className="sr-only">
            Filter by source record
          </label>
          <select
            id="materials-surface"
            value={surface}
            onChange={(e) => setSurface(e.target.value as MaterialSurface | 'all')}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All records</option>
            {(config?.surfaces ?? []).map((s) => (
              <option key={s} value={s}>
                {SURFACE_LABEL[s] ?? s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <p className="py-8 text-sm text-gray-500">Loading materials...</p>}

      {isError && (
        <div className="py-8">
          <p className="text-sm text-red-600">
            {materialErrorCode(error) === 'not_configured'
              ? 'PitchLess is not connected in this environment.'
              : 'Could not load materials from PitchLess.'}
          </p>
          {materialErrorCode(error) !== 'not_configured' && (
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              Try again
            </button>
          )}
        </div>
      )}

      {!isLoading && !isError && materials.length === 0 && (
        <p className="py-8 text-sm text-gray-500">
          {review === 'all' && surface === 'all'
            ? 'No materials yet. Create one from an estimate, opportunity or company.'
            : 'No materials match this filter.'}
        </p>
      )}

      {materials.length > 0 && (
        <ul
          data-testid="materials-library-list"
          className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white"
        >
          {materials.map((m) => {
            const route = sourceRoute(m);
            const ready = m.currentVersionId !== null && !isBusy(m.status);
            return (
              <li key={m.id} className="px-4 py-3 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {m.title || 'Untitled material'}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <MaterialStatusBadge status={m.status} reviewStatus={m.reviewStatus} />
                    {m.sourceEntityType && (
                      <span className="text-xs text-gray-500">
                        {route ? (
                          <Link to={route} className="text-blue-600 hover:text-blue-700">
                            From {SURFACE_LABEL[m.sourceEntityType as MaterialSurface] ?? m.sourceEntityType}
                          </Link>
                        ) : (
                          `From ${m.sourceEntityType}`
                        )}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {ready && (
                    <button
                      type="button"
                      disabled={filePending}
                      onClick={() => void download(m.id, 'pdf', m.currentVersionId ?? undefined)}
                      className="px-3 py-2 min-h-[44px] text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Download PDF
                    </button>
                  )}
                  {config?.appUrl && (
                    <a
                      href={`${config.appUrl}/artifacts/${encodeURIComponent(m.id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap"
                    >
                      Open in PitchLess
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
